import React from "react";
import { useNavigate } from "react-router-dom";
import "./styles.css";
import Button from "../../components/shared/Button";
import settingsIcon from "../../assets/icons/newDesign/settings.svg";
import backIcon from "../../assets/icons/newDesign/back.svg";
import HomeIcon from "../../assets/icons/home.svg";
import { useLocalization } from "../../context/LocalizationContext";
import { useAuth } from "../../context/AuthContext";
import ProfileSection from "../../components/shared/ProfileSection";

export interface HeaderProps {
  title?: string;
  subtitle?: string;
  /** Status indicator with green dot above the title */
  statusText?: string;
  statusType?: "success" | "danger";
  showBackButton?: "LEFT" | "RIGHT";
  onBack?: () => void;
  showSettingsButton?: boolean;
  showHomeButton?: boolean;
  showProfile?: boolean;

}

const Header: React.FC<HeaderProps> = ({
  title,
  subtitle,
  statusText,
  statusType = "success",
  showBackButton = false,
  onBack,
  showSettingsButton = false,
  showHomeButton = false,
  showProfile
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t, isRTL } = useLocalization();

  const handleBack = onBack ?? (() => navigate(-1));
  
  const shouldShowProfile = showProfile && isAuthenticated;

  // ── Left slot: back button › status › subtitle › nothing ──────────────────
  const renderLeft = () => {
    if (showBackButton === "LEFT") {
      return (
        <Button
          variant="secondary"
          size="icon"
          icon={backIcon}
          onClick={handleBack}
          iconStyle={isRTL ? { transform: "rotate(180deg)" } : undefined}
        />
      );
    }
    if (statusText) {
      return (
        <div className="logged-in-status">
          <span className={`status-dot ${statusType === "danger" ? "status-dot--danger" : ""}`} />
          <span className="status-text">{statusText}</span>
        </div>
      );
    }
    if (subtitle) {
      return <p className="header-subtitle">{subtitle}</p>;
    }
    return null;
  };

  // ── Right slot: profile › action buttons ──────────────────────────────────
  const renderRight = () => {
    if (shouldShowProfile) return <ProfileSection />;

    return (
      <div className="header-actions">
        {showSettingsButton && (
          <Button
            variant="secondary"
            size="icon"
            icon={settingsIcon}
            onClick={() => navigate("/settings")}
          />
        )}
        {showHomeButton && (
          <Button
            variant="secondary"
            size="icon"
            icon={HomeIcon}
            onClick={() => navigate("/dashboard")}
          />
        )}
        {
          showBackButton === "RIGHT" && (
            <Button
              size="icon"
              variant="secondary"
              icon={backIcon}
              onClick={handleBack}
              iconStyle={isRTL ? undefined : { transform: "rotate(180deg)" }}
            />
          )
        }
      </div>
    );
  };

  // ── Layout ─────────────────────────────────────────────────────────────────
  return (
    <div className="app-header">
      <div className="header-content">
        <div className="left-section">
          {renderLeft()}
          {title && <h1 className="welcome-text">{title}</h1>}
        </div>
        {renderRight()}
      </div>
    </div>
  );
};

export default Header;
